import React, { Fragment } from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose, defaultProps, withProps } from 'recompose';
import { connect } from 'react-redux';
import { get, map } from 'lodash';
import Crumb from './Crumb';

import './BreadCrumbsV2.scss';

function createCLPBreadcrumbSchema(crumbs = [], baseUrl) {
	const ROOT_URL = baseUrl || 'https://www.selfstorage.com';
	const ensureTrailingSlash = (url) =>
		url && !url.endsWith('/') ? `${url}/` : url;
	const filtered = crumbs.filter((crumb) => crumb?.props?.label);

	const itemListElement = filtered.map((crumb, index) => {
		const rawHref = (get(crumb, 'props.href', '') || '').split('?').shift();
		const isAbsolute = /^https?:\/\//i.test(rawHref);
		const absoluteUrl = !rawHref
			? ROOT_URL
			: isAbsolute
				? rawHref
				: `${ROOT_URL.replace(/\/$/, '')}${rawHref.startsWith('/') ? rawHref : `/${rawHref}`}`;
		const idUrl = ensureTrailingSlash(absoluteUrl);

		return {
			'@type': 'ListItem',
			position: index + 1,
			item: {
				'@id': idUrl,
				name: crumb.props.name || crumb.props.label,
				url: idUrl,
			},
		};
	});

	const cityCrumb = filtered.find(
		(c) => c?.props?.className === 'sublocality',
	);
	let landingUrl = '';

	if (cityCrumb) {
		const raw = (get(cityCrumb, 'props.href', '') || '').split('?').shift();
		const isAbs = /^https?:\/\//i.test(raw);
		const abs = !raw
			? ROOT_URL
			: isAbs
				? raw
				: `${ROOT_URL.replace(/\/$/, '')}${raw.startsWith('/') ? raw : `/${raw}`}`;
		landingUrl = ensureTrailingSlash(abs);
	} else {
		landingUrl =
			get(
				itemListElement,
				`[${itemListElement.length - 1}].item['@id']`,
				'',
			) || ensureTrailingSlash(ROOT_URL);
	}

	const breadcrumbListId = `${landingUrl.replace(/#.*$/, '')}#breadcrumb`;
	return {
		'@type': 'BreadcrumbList',
		'@id': breadcrumbListId,
		itemListElement,
	};
}

function createCrumbSchema(crumbs) {
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: crumbs
			.filter((crumb) => crumb.props.label)
			.map((crumb, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				name: crumb.props.name || crumb.props.label,
				item: get(crumb, 'props.id', get(crumb, 'props.to', ''))
					.split('?')
					.shift(),
			})),
	};
}

export const enhance = compose(
	defaultProps({
		location: {},
		searchType: 'storage',
		baseUrl: 'https://www.selfstorage.com',
	}),
	connect((state) => ({
		baseUrl: (state && state.app && state.app.baseUrl) || undefined,
	})),
	withProps(
		({
			location,
			crumbs = [],
			cityUrl,
			stateUrl,
			baseUrl,
			tertiary,
			isCLPPage,
		}) => {
			const newCrumbs = [];
			const { fullState, subLocality, city } = location;
			const sub = subLocality || city;

			const normalizedBase = (baseUrl || '').replace(/\/$/, '');
			const joinUrl = (base, path) =>
				`${(base || '').replace(/\/$/, '')}/${(path || '').replace(/^\//, '')}`;

			newCrumbs.push(
				<Crumb
					href={normalizedBase + '/'}
					id={normalizedBase + '/'}
					key="base"
					label="Home"
					name="Home"
					baseUrl={baseUrl}
					tertiary={tertiary}
				/>,
			);

			if (stateUrl) {
				newCrumbs.push(
					<Crumb
						key="state"
						href={joinUrl(normalizedBase, stateUrl)}
						id={joinUrl(normalizedBase, stateUrl)}
						isLast={!cityUrl && !crumbs.length}
						label={`${fullState} Self Storage`}
						name={`${fullState} Self Storage`}
						className="state"
						tertiary={tertiary}
					/>,
				);
			}

			if (cityUrl) {
				const isLast = !crumbs.length;
				newCrumbs.push(
					<Crumb
						key="sublocality"
						className="sublocality"
						href={joinUrl(normalizedBase, cityUrl)}
						isLast={isLast}
						label={`${sub} Storage`}
						name={sub}
					>
						{sub}
					</Crumb>,
				);
			}

			return {
				crumbs: [...newCrumbs, ...crumbs],
				crumbJSON: isCLPPage
					? createCLPBreadcrumbSchema(
							[...newCrumbs, ...crumbs],
							baseUrl,
						)
					: createCrumbSchema([...newCrumbs, ...crumbs]),
			};
		},
	),
);

export function BreadCrumbsV2({ crumbs, crumbJSON, className }) {
	return (
		<Fragment>
			<ul
				className={classnames('ss-breadcrumbs-v2', className)}
				id="breadcrumb"
			>
				{map(crumbs, (crumb) => (
					<li
						className="ss-breadcrumb-v2"
						key={crumb.key}
					>
						{crumb}
					</li>
				))}
			</ul>
			<Helmet>
				<script type="application/ld+json">
					{JSON.stringify(crumbJSON)}
				</script>
			</Helmet>
		</Fragment>
	);
}

BreadCrumbsV2.propTypes = {
	className: PropTypes.string,
	crumbJSON: PropTypes.object,
	crumbs: PropTypes.node,
};

export default enhance(BreadCrumbsV2);
